import React, { useEffect, useRef, useState } from 'react';
import SlidePanel from '../../shared/SlidePanel';
import toast from '../../shared/Toast';
import { Icon } from '../BaileyBits';
import { hapticError, hapticSuccess, hapticTick } from '../../../_utils/haptics';
import { groupVoices, voiceInUse, voiceTitle, useSample, stopSample } from './voices';
import { errorText } from './api';

/**
 * Bailey's voice on the phone. Grouped by accent, British first, each with
 * the vibe the server describes and a sample to play when there is one.
 * Nothing changes on the line until Use is pressed.
 */

/** Play or stop one voice's sample. One plays at a time, app-wide. */
export function PlayButton({ voice, sample, className = '' }) {
    const on = sample.playing === voice.id;
    return (
        <button
            type="button"
            className={`fd-play${on ? ' is-on' : ''} ${className}`}
            aria-label={on ? `Stop ${voice.name}` : `Hear ${voice.name}`}
            aria-pressed={on}
            onClick={(e) => { e.stopPropagation(); hapticTick(); sample.toggle(voice); }}
        >
            {on ? <span className="fd-bars" aria-hidden="true"><i /><i /><i /></span> : <Icon.Play size={13} />}
        </button>
    );
}

/** The row under How I talk: which voice, a play button, and the panel on tap. */
export function VoiceRow({ fd, onOpen }) {
    const sample = useSample();
    const { voice, isDefault } = voiceInUse(fd.settings, fd.voices);
    const hasNumber = !!fd.settings.voiceNumber;
    const title = voice ? voiceTitle(voice) : fd.settings.voiceId ? 'Your chosen voice' : 'Choose my voice';
    const sub = !hasNumber
        ? 'Callers hear this once your phone is set up'
        : ['My voice on the phone', voice && voice.vibe, isDefault && 'the default'].filter(Boolean).join(' · ');
    return (
        <div className="bly-row fd-voice-row">
            {voice && voice.sample
                ? <PlayButton voice={voice} sample={sample} className="is-tile" />
                : <span className="bly-tile" aria-hidden="true"><Icon.Headset size={20} /></span>}
            <button type="button" className="fd-voice-open" onClick={() => { hapticTick(); onOpen(); }} aria-label={`My voice on the phone: ${title}. Change`}>
                <span className="bly-row-copy"><b>{title}</b><small>{sub}</small></span>
                <span className="bly-chev" aria-hidden="true"><Icon.Chevron /></span>
            </button>
        </div>
    );
}

/** A line under the phone greeting or the phone switch: who callers hear, and Change. */
export function VoiceLine({ fd, lead = '', onChange }) {
    const sample = useSample();
    const { voice } = voiceInUse(fd.settings, fd.voices);
    if (!fd.voices || fd.voices.error || !fd.voices.voices.length) return null;
    return (
        <div className="fd-voice-line">
            {voice && voice.sample && <PlayButton voice={voice} sample={sample} className="is-small" />}
            <span>{lead}{voice ? <><b>{voice.name}</b>{voice.accent ? `, ${voice.accent}` : ''}</> : 'your chosen voice'}</span>
            <button type="button" className="bly-link" onClick={() => { hapticTick(); onChange(); }}>Change</button>
        </div>
    );
}

export default function VoicePanel({ fd, onClose }) {
    const sheet = useRef(null);
    const alive = useRef(true);
    const sample = useSample();
    useEffect(() => () => { alive.current = false; stopSample(); }, []);
    const list = fd.voices;
    const voices = (list && list.voices) || [];
    const inUseId = String(fd.settings.voiceId || (list && list.defaultId) || '');
    const [pick, setPick] = useState(inUseId);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    // The list arrived after the panel opened: start on the voice in use.
    useEffect(() => { if (!pick && inUseId) setPick(inUseId); }, [inUseId]); // eslint-disable-line react-hooks/exhaustive-deps

    const picked = voices.find((v) => v.id === pick) || null;
    const changed = !!picked && pick !== inUseId;
    const anySample = voices.some((v) => v.sample);
    const hasNumber = !!fd.settings.voiceNumber;
    const close = () => { if (sheet.current) sheet.current.handleClose(); else onClose(); };

    const use = async () => {
        if (!changed || busy) return;
        setBusy(true); setError('');
        try {
            await fd.save({ voiceProvider: picked.provider || '', voiceId: picked.id });
            hapticSuccess();
            toast.success(hasNumber ? `${picked.name} will answer your calls` : 'Voice saved');
            if (alive.current) close();
        } catch (e) {
            if (!alive.current) return;
            hapticError(); setError(errorText(e, 'That didn’t save. Try again.')); setBusy(false);
        }
    };

    const footer = changed ? (
        <div className="fd-panel-footer">
            <button type="button" className="sp-btn-secondary" disabled={busy} onClick={() => { hapticTick(); close(); }}>Cancel</button>
            <button type="button" className="sp-btn-primary" disabled={busy || !fd.ready} onClick={use}>{busy ? 'Saving…' : `Use ${picked.name}`}</button>
        </div>
    ) : (
        <div className="fd-panel-footer">
            <button type="button" className="sp-btn-primary" onClick={() => { hapticTick(); close(); }}>Done</button>
        </div>
    );

    return (
        <SlidePanel ref={sheet} title="My voice" onClose={onClose} width={480} className="bly-panel fd-form" footer={footer}>
            <div className="sp-form fd-voices">
                {error && <p className="sp-general-error" role="alert">{error}</p>}
                <p className="fd-panel-lead">
                    {hasNumber ? 'The voice callers hear when I answer your phone.' : 'The voice callers will hear once I’m answering your phone.'}
                    {anySample ? ' Press play to hear one.' : ''}
                </p>

                {list === null && (
                    <div className="fd-voice-wait" aria-busy="true" aria-label="Loading voices">
                        {[0, 1, 2, 3].map((i) => <span key={i} className="fd-wait" />)}
                    </div>
                )}

                {list && list.error && (
                    <div className="fd-voice-fail">
                        <p className="sp-general-error" role="alert">I couldn’t load the voices just now.</p>
                        <button type="button" className="sp-btn-secondary" onClick={() => { hapticTick(); fd.reloadVoices(); }}>Try again</button>
                    </div>
                )}

                {list && !list.error && !voices.length && (
                    <p className="sp-hint">I use a standard voice for now. More are on their way.</p>
                )}

                {groupVoices(voices).map((g) => (
                    <div key={g.accent} className="fd-voice-group" role="radiogroup" aria-label={`${g.accent} voices`}>
                        <span className="sp-label">{g.accent}</span>
                        {g.voices.map((v) => {
                            const on = pick === v.id;
                            const tag = v.id === inUseId ? 'In use' : list && v.id === list.defaultId ? 'Default' : '';
                            return (
                                <div key={v.id} className={`fd-voice${anySample ? ' has-play' : ''}`}>
                                    <button type="button" role="radio" aria-checked={on} className={`fd-pick-row fd-voice-pick${on ? ' is-on' : ''}`} onClick={() => { hapticTick(); setPick(v.id); setError(''); }}>
                                        <span className="fd-radio" aria-hidden="true" />
                                        <span className="fd-voice-copy"><b>{v.name}</b>{v.vibe && <small>{v.vibe}</small>}</span>
                                        {tag ? <span className={`fd-voice-tag${tag === 'In use' ? ' is-use' : ''}`}>{tag}</span> : <span />}
                                    </button>
                                    {anySample && (v.sample ? <PlayButton voice={v} sample={sample} /> : <span aria-hidden="true" />)}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </SlidePanel>
    );
}

